/**
 * @auth 赖传峰
 * @date 2022/1/19
 */

import { useNuxtApp, watch } from '#imports'
import UAParser from 'ua-parser-js'
import { getAreaCountryCode } from '~/api'
import { cookieAttribution, storageReferer } from '~/middleware/attribution.global'
import { LoginScene, OSName, RouteQueryKey, StorageKey } from '~/types/enum'
import { getUserIdFromCredentials } from '~/utils/getUserIdFromCredentials'

/* 哪些 action「actions」，需要上报什么样的登录场景「scenes」，以什么值上报「key」 */
const getClientType = () => {
  const os = new UAParser().getOS().name
  switch (os) {
    case OSName.IOS:
      return 'ios'
    case OSName.ANDROID:
      return 'android'
    case OSName.MAC_OS:
      return 'mac'
    default:
      return 'pc'
  }
}

/* 哪些 action「actions」，需要上报什么样的登录场景「scenes」，以什么值上报「key」 */
const shouldReportLoginSceneActions: Readonly<
  {
    actions: string[]
    scenes: LoginScene[]
    key: string
  }[]
> = [
  {
    actions: [
      'web_login_page_show',
      'web_login_page_click',
      'web_login_page_close',
      'web_phone_login_code_sending_result',
      'web_email_login_page_show',
      'web_email_login_page_click',
      'web_email_register_page_show',
      'web_email_register_page_click',
      'qrcode_page_show',
      'qrcode_page_click',
      'web_login_out',
      'web_login_request',
      'web_login_success',
      'web_login_fail',
      'web_free_premium_pop_show',
    ],
    scenes: Object.values(LoginScene),
    key: 'scene',
  },
  {
    actions: [
      'web_add_number_limit_pop_show',
      'web_add_number_limit_pop_click',
      'web_yunpan_space_limit_pop_show',
      'web_yunpan_space_limit_pop_click',
    ],
    scenes: [LoginScene.CLOUD_PAGE, LoginScene.LANDING_PAGE],
    key: 'aidfrom',
  },
]

const appendLoginScenes2extData = (action: string, extData: Record<string, unknown>) => {
  const loginScene = window.localStorage?.getItem(StorageKey.LOGIN_SCENE)
  if (!loginScene) return extData
  const config = shouldReportLoginSceneActions.find(el => el.actions.includes(action))
  if (loginScene && config && (config.scenes as string[]).includes(loginScene)) {
    return { ...extData, [config.key]: loginScene }
  }
  return extData
}

/**
 * 从浏览器扩展程序打开云盘，携带 scene
 * add_button  保存按钮
 * plugin_entry  地址栏-插件入口
 */
export const loginSceneFromExtension = () => {
  const extensionSceneRange = [LoginScene.ADD_BUTTON, LoginScene.PLUGIN_ENTRY] as string[]
  const scene = new URLSearchParams(window.location.search).get(RouteQueryKey.SCENE)
  if (scene && extensionSceneRange.includes(scene)) return scene
  return undefined
}

/**
 * 挂载登录组件时，记录 login scene，表示登录框被调起的场景
 */
export const setLoginScene = () => {
  if (import.meta.server) return

  let loginScene = LoginScene.OFFICIAL_PAGE
  if (window.location.href.includes('/s/')) {
    loginScene = LoginScene.SHARE_PAGE
  } else if (window.location.href.includes('url-checker')) {
    loginScene = LoginScene.URL_CHECKER
  }

  loginScene && localStorage?.setItem(StorageKey.LOGIN_SCENE, loginScene) // 在 Mobile Safari 中,localStorage 初始可能为空
}

export const appendGlobalExtData: {
  (data: Record<string, unknown>): void
  dataCache: Record<string, unknown>[]
} = data => {
  if (typeof window === 'undefined' || !window.ppa) {
    appendGlobalExtData.dataCache.push(data)
    return
  }
  appendGlobalExtData.dataCache.concat(data).forEach(value => {
    window.ppa.push({
      type: 'globalExtData',
      data: value,
    })
  })
  appendGlobalExtData.dataCache = []
}
appendGlobalExtData.dataCache = [] // 上报还未初始化时，如果调用上报，将数据缓存，初始化完成后再上报

const getOriginDeviceId = () => {
  const deviceId = window.localStorage.getItem('deviceid') || window.localStorage.getItem('_deviceid')
  const originDeviceId = deviceId?.split('.').pop()?.substring(0, 32)
  return originDeviceId || ''
}

export default async () => {
  if (typeof window === 'undefined') return
  let dataCache: Record<string, unknown>[] = []
  window.stat = (category, action, extData = {}) => {
    const args = {
      category,
      action,
      extdata: appendLoginScenes2extData(action, extData),
    }

    if (!window.ppa) {
      dataCache.push(args)
      return
    }

    dataCache.concat(args).forEach((value: Record<string, unknown>) => {
      window.ppa.push({ type: 'event', ...(value as typeof args) })

      /* http://wiki.xunlei.cn/pages/viewpage.action?pageId=96442437 */
      if (value.category === 'web_yunpan_login') {
        const eventName: GACustomEventName | undefined = (() => {
          switch (value.action) {
            case 'web_login_page_show':
              return 'login_page_impress'
            case 'web_login_page_click':
              return 'login_page_click'
            case 'web_login_success':
              return 'login'
            default:
              return undefined
          }
        })()
        eventName && window.gtag?.('event', eventName)
      }
    })
    dataCache = []
  }
  appendGlobalExtData({
    client_type: getClientType(),
    language_system: navigator.language,
    language_web: useNuxtApp().$i18n.locale.value, // 如果用户修改，应该如何？
    userType: 0, // 未登录
    source: cookieAttribution.get().source,
    scene: 'office_page',
    referer_list: encodeURIComponent(storageReferer.get().join()),
    deviceid: getOriginDeviceId(),
    userid: getUserIdFromCredentials(),
  })
  getAreaCountryCode().then(data => appendGlobalExtData({ country: data?.data?.value?.data?.countryCode }))
  watch(useNuxtApp().$i18n.locale, val => appendGlobalExtData({ language_web: val }))
}
